import { useEffect, useMemo, useState } from 'react';
import {
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  canAvgRhythmEvents,
  canShowCountBeatEvents,
  canShowDurationEvents,
  LIST_RUN_EVENT,
} from '@/shared/config';
import { selectEventFilter, useReportStore } from '@/entities/report';
import type { FilterEventsProps } from '.';

/**
 * Backs the events filter control with its stored filter, open state and a human-readable summary. Reads and writes the tab-keyed event filter in the report store, derives from the active event type which of the beat count, rhythm average, duration and low-HR options apply, closes the popover whenever that event type changes, and memoises a count plus description nodes for the active filters.
 *
 * @param props - Hook props carrying activeButton and keyRecoil.
 * @returns An object carrying eventFilter, setEventFilter, isOpen, setIsOpen, the isShowCountBeats, optionrhythm, isShowFilterDuration and isShowAvgHrBelow100 flags, onOpenChange and eventFilterInfos.
 */
const useFilterEvents = ({
  activeButton,
  keyRecoil,
}: Pick<FilterEventsProps, 'activeButton' | 'keyRecoil'>) => {
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));
  const setEventFilterAction = useReportStore((s) => s.actions.setEventFilter);
  /**
   * Writes the event filter for the current report tab. Calls the store's setEventFilter action with the tab key and the supplied filter value.
   *
   * @param v - The filter value forwarded to the store action.
   * @returns The result of the store's setEventFilter action.
   */
  const setEventFilter = (v: Parameters<typeof setEventFilterAction>[1]) =>
    setEventFilterAction(keyRecoil, v);

  const [isOpen, setIsOpen] = useState(false);

  // activeButton's atom type also allows TBeatHrActiveTab, wider than these arrays' element type - narrowed for the .includes() checks.
  const isShowCountBeats = canShowCountBeatEvents.includes(
    activeButton as TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM,
  );
  const optionrhythm = canAvgRhythmEvents.includes(activeButton as TYPE_RHYTHM_EVENT_ENUM);
  const isShowFilterDuration = canShowDurationEvents.includes(
    activeButton as TYPE_RHYTHM_EVENT_ENUM,
  );
  const isShowAvgHrBelow100 = LIST_RUN_EVENT.includes(
    activeButton as TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM,
  );

  /**
   * Tracks whether the events filter popover is open. Stores the new open flag in local state.
   *
   * @param open - Whether the popover is now open.
   * @returns Nothing.
   */
  const onOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const eventFilterInfos = useMemo(() => {
    let count = 0;
    const textHtml: any[] = [];
    Object.keys(eventFilter).forEach((key) => {
      if (eventFilter[key as keyof typeof eventFilter]) {
        if (key === 'isHideReviewed') {
          textHtml.push(<div>Hide reviewed events</div>);
          count += 1;
        }
        if (key === 'isCapture') {
          textHtml.push(<div>Show events have captured strips</div>);
          count += 1;
        }
        if (key === 'isAvgHrBelow100' && isShowAvgHrBelow100) {
          textHtml.push(<div>HR &lt; 100 bpm</div>);
          count += 1;
        }
        if (key === 'sortOrder') {
          textHtml.push(
            <div>
              {`Sort events by ${eventFilter.sortBy === 'avgHr' ? 'average HR:' : eventFilter.sortBy === 'duration' ? 'duration:' : 'beat count:'} ${eventFilter[key as keyof typeof eventFilter] === 'asc' ? 'ascending' : 'descending'}`}
            </div>,
          );
          count += 1;
        }
      }
    });
    return {
      count,
      textHtml,
    };
  }, [eventFilter, isShowCountBeats, activeButton, isShowAvgHrBelow100]);

  useEffect(() => {
    setIsOpen(false);
  }, [activeButton]);

  return {
    eventFilter,
    setEventFilter,
    isOpen,
    setIsOpen,
    isShowCountBeats,
    optionrhythm,
    isShowFilterDuration,
    isShowAvgHrBelow100,
    onOpenChange,
    eventFilterInfos,
  };
};

export default useFilterEvents;
